import { Component } from 'opus-angular-seed-core';

@Component({
    bindings: {
        model: '=',
        onParse: '&?',
        onDelete: '&?',
        disabled: '<?',
        skipHeader: '<?'
    },
    template: `
    <div class="dropzone" ng-class="{ 'is-dragover': vm.dragOver, 'disabled': vm.disabled }">
      <input style="height: 100%" type="file" class="form-control" accept=".csv" ng-disabled="vm.disabled">
      <p><strong>{{ 'general.Click to upload' | translate }}</strong> {{ 'general.or drag and drop a CSV file' | translate }}</p>
      <p>{{ 'general.CSV only' | translate }}</p>
    </div>

    <div class="dropzone" ng-if="vm.busy || vm.error || (vm.model && vm.model.length)">
      <div class="dropzone-item">
        <img src="assets/css/ico-pdf.png" alt="csv">
        <div class="dropzone-item__inner">
          <div class="dropzone-item__title">
            <p>{{ vm.filename || '—' }}</p>
            <a class="btn delete icononly" ng-click="vm.clear()" ng-if="!vm.disabled">Remove</a>
          </div>
          <p class="dropzone-item__meta" ng-if="vm.busy">Reading…</p>
          <p class="dropzone-item__meta text-danger" ng-if="vm.error">{{ vm.error }}</p>
          <p class="dropzone-item__meta" ng-if="!vm.busy && !vm.error && vm.model">
            {{ vm.model.length }} {{ 'general.row(s) parsed' | translate }}
          </p>
          <div class="dropzone-item__progress" ng-if="vm.busy">
            <span style="width: 50%">50%</span>
          </div>
        </div>
      </div>
    </div>
  `
})
export default class {
    constructor ($element, $timeout) {
        this.$element = $element;
        this.$timeout = $timeout;

        // UI state
        this.busy = false;
        this.error = '';
        this.filename = '';
        this.dragOver = false;

        this._bound = false;
        this._listeners = null;
    }

    $onInit () {
        if (!Array.isArray(this.model)) this.model = null;
        if (typeof this.skipHeader === 'undefined') this.skipHeader = false;
        this.$timeout(() => this._bind(), 0);
    }

    $onDestroy () {
        this._unbind();
    }

    clear () {
        const filename = this.filename;
        this.busy = false;
        this.error = '';
        this.filename = '';
        this.model = null;
        const input = this._fileInput();
        if (input) input.value = '';

        if (this.onDelete) {
            this.onDelete({ filename });
        }
    }

    /* ---------------- private ---------------- */

    _dropzone () {
        // the first .dropzone in this component is the picker area
        return this.$element[0].querySelector('.dropzone');
    }

    _fileInput () {
        return this.$element[0].querySelector('input[type="file"]');
    }

    _bind () {
        if (this._bound) return;
        const dz = this._dropzone();
        const input = this._fileInput();
        if (!dz || !input) return;

        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const onEnterOver = (e) => {
            prevent(e); this.dragOver = true;
            this._tick();
        };
        const onLeave = (e) => {
            prevent(e);
            this.dragOver = false;
            this._tick();
        };
        const onDrop = (e) => {
            prevent(e);
            this.dragOver = false;
            const file = (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) || null;
            if (file && !this.disabled) this._handleFile(file);
            this._tick();
        };
        const onChange = (e) => {
            const file = (e.target.files && e.target.files[0]) || null;
            if (file && !this.disabled) this._handleFile(file);
        };

        ['dragenter', 'dragover'].forEach(ev => dz.addEventListener(ev, onEnterOver));
        dz.addEventListener('dragleave', onLeave);
        dz.addEventListener('drop', onDrop);
        input.addEventListener('change', onChange);

        this._listeners = { dz, input, onEnterOver, onLeave, onDrop, onChange };
        this._bound = true;
    }

    _unbind () {
        if (!this._listeners) return;
        const { dz, input, onEnterOver, onLeave, onDrop, onChange } = this._listeners;
        ['dragenter', 'dragover'].forEach(ev => dz.removeEventListener(ev, onEnterOver));
        dz.removeEventListener('dragleave', onLeave);
        dz.removeEventListener('drop', onDrop);
        input.removeEventListener('change', onChange);
        this._listeners = null;
        this._bound = false;
    }

    _tick () {
        this.$timeout(() => { }, 0);
    }

    _handleFile (file) {
        this.error = '';
        this.filename = file && file.name || '';
        if (!this._isCsv(file)) {
            this.error = 'Please upload a .csv file.';
            this.model = null;
            return;
        }

        this.busy = true;
        const reader = new FileReader();
        reader.onload = () => {
            let text = reader.result || '';
            if (text && text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip BOM
            try {
                const parsed = this._parseCSV(text);
                const header = (parsed && parsed.length) ? parsed[0] : null;
                const rows = this.skipHeader ? parsed.slice(1) : parsed;

                this.model = rows;
                this.error = '';
                // onParse now also provides 'header' (safe to ignore in parent)
                if (this.onParse) this.onParse({ rows, filename: this.filename, header });
            }
            catch (e) {
                this.model = null;
                this.error = `Failed to parse CSV: ${e && e.message ? e.message : e}`;
            }
            finally {
                this.busy = false;
                this._tick();
            }
        };
        reader.onerror = () => {
            this.busy = false;
            this.error = 'Could not read file.';
            this.model = null;
            this._tick();
        };
        reader.readAsText(file, 'utf-8');
    }

    _isCsv (file) {
        const nameOk = /\.csv$/i.test(file.name || '');
        const typeOk = /csv|comma-separated-values|text\/plain/i.test(file.type || '');
        return nameOk || typeOk;
    }

    /**
     * CSV parser:
     * - Handles quoted fields
     * - Handles escaped quotes ("")
     * - Auto-detects delimiter from [comma, semicolon, tab, pipe]
     */
    _parseCSV (str) {
        const delim = this._detectDelimiter(str);
        const rows = [[]];
        let cur = '';
        let i = 0;
        let inQuotes = false;

        while (i < str.length) {
            const c = str[i];

            if (inQuotes) {
                if (c === '"') {
                    if (str[i + 1] === '"') {
                        cur += '"'; i += 2; continue;
                    }
                    inQuotes = false; i++; continue;
                }
                cur += c; i++; continue;
            }

            if (c === '"') {
                inQuotes = true; i++; continue;
            }
            if (c === '\r') {
                i++; continue;
            }
            if (c === '\n') {
                rows[rows.length - 1].push(cur); cur = ''; rows.push([]); i++; continue;
            }
            if (c === delim) {
                rows[rows.length - 1].push(cur); cur = ''; i++; continue;
            }

            cur += c; i++;
        }
        rows[rows.length - 1].push(cur);

        // drop trailing empty row if ends with newline
        if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') {
            rows.pop();
        }
        return rows;
    }

    _detectDelimiter (str) {
        // look at first 5 lines
        const sample = str.split(/\r?\n/).slice(0, 5).join('\n');
        const candidates = [',', ';', '\t', '|'];
        let best = ',';
        let bestCount = -1;
        candidates.forEach(d => {
            // rough count, ignoring quotes (good enough for detection)
            const count = (sample.match(new RegExp(`\\${d}`, 'g')) || []).length;
            if (count > bestCount) {
                bestCount = count; best = d;
            }
        });
        return best;
    }
}



// WEBPACK FOOTER //
// ./src/app/components/upload/csv/index.js